import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner } from "react-bootstrap";
import ActionComp from "../layouts/ActionComp";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import GridMaster from "../masters/GridMaster";
import ActionRenderer from "../masters/ActionRenderer";
import AddEditEnquiryTender from "./AddEditEnquiryTender";
import SingleValuePopUp from "../utils/SingleValuePopUp";
import AddEditEnquiryFromContract from "./AddEditEnquiryFromContract";
import AddEditEnquiryFromDisposal from "./AddEditEnquiryFromDisposal";
import MultiValuePopUp from "../utils/MultiValuePopUp";
import UpdateDueDateModal from "./UpdateDueDateModal";
import { AuthContext } from "../../contexts/AuthContext";
import { menuActions } from "../../store/menu-slice";
import { useDispatch } from "react-redux";
import { searchActions } from "../../store/grid-slice";
import { formatDateInDDMMYYYY } from "../../functions/common-functions";
import { getAllEnquiryTenders, deleteEnquiryTender, addEnquiryTender, getAllEnquiryTendersActive, addEnquiryTenderMail, linkCustomersToEnquiry } from "../../services/enquiry-tender-service";
import { getCustomerUsersByCode } from "../../services/user-service";
import { addLogDetails } from "../../services/logger-entry-service";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ALERT_PARAMS } from "../../utils/constant";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";

const EnquiryTenderDashboard = () => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [resetFilter, setResetFilter] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showContractPopup, setShowContractPopup] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);
  const [showDisposalForm, setShowDisposalForm] = useState(false);
  const [action, setAction] = useState("");
  const [contractAction, setContractAction] = useState("");
  const [rowData, setRowData] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedDisposal, setSelectedDisposal] = useState(null);
  const [disposalAction, setDisposalAction] = useState("");
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [reload, setReload] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    dispatch(menuActions.setMenu("Enquiry/Tender Dashboard"));
    dispatch(searchActions.setSearch(""));
  }, [dispatch]);

  useEffect(() => {
    fetchEnquiryTenders();
  }, [reload]);

  const fetchEnquiryTenders = async () => {
    setLoading(true);
    try {
      const enquiries = await getAllEnquiryTendersActive();
      // Filter enquiries by allowed statuses
      const allowedStatuses = ["Created", "Not Assigned", "Mail Not Sent", "Live"];
      const filteredEnquiries = enquiries.filter(enquiry =>
        allowedStatuses.includes(enquiry.EnquiryStatus)
      );
      setData(filteredEnquiries);
      setResetFilter(!resetFilter);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    }
    setLoading(false);
  };

  const handleSendMail = async () => {
    if (selectedRows.length === 0) {
      MySwal.fire({
        text: "Please select at least one enquiry",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
      return;
    }

    const dueDates = [...new Set(selectedRows.map(row => row.DueDate))];
    if (dueDates.length > 1) {
      MySwal.fire({
        text: "For selected enquiries, Due Date should be same.",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
      return;
    }

    const invalidEnquiries = selectedRows.filter(row => {
      if (!row.DueDate) return true;
      if (!row.LinkedCustomers || !Array.isArray(row.LinkedCustomers) || row.LinkedCustomers.length === 0) return true;
      return false;
    });

    if (invalidEnquiries.length > 0) {
      const noDueDate = invalidEnquiries.some(row => !row.DueDate);
      const noCustomer = invalidEnquiries.some(row => !row.LinkedCustomers || !Array.isArray(row.LinkedCustomers) || row.LinkedCustomers.length === 0);

      if (noDueDate && noCustomer) {
        MySwal.fire({
          text: "Please select Due Date and link a Customer before sending the email.",
          imageUrl: errorImage,
          customClass: "swal-success"
        });
      } else if (noDueDate) {
        MySwal.fire({
          text: "Please select Due Date",
          imageUrl: errorImage,
          customClass: "swal-success"
        });
      } else {
        MySwal.fire({
          text: "Please Link the Customer",
          imageUrl: errorImage,
          customClass: "swal-success"
        });
      }
      return;
    }

    MySwal.fire({
      text: "Do you want to send mail to customers?",
      confirmButtonText: "Send",
      title: "Send Mail",
      ...ALERT_PARAMS
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const result = await addEnquiryTenderMail({ enquiries: selectedRows });
          setLoading(false);
          if (result.status) {
            MySwal.fire({
              text: "Email sent to Customers",
              imageUrl: successImage,
              customClass: "swal-success"
            });
            await addLogDetails(
              { Action: "SendMail", EnquiryCount: selectedRows.length },
              "EnquiryTenderDashboard",
              "SendMail",
              "MailLog",
              { userName: context.userName, userId: context.userId },
              "Transaction"
            );
          } else {
            MySwal.fire({
              text: result.message || "Unable to send email",
              imageUrl: errorImage,
              customClass: "swal-success"
            });
          }
        } catch (error) {
          setLoading(false);
          MySwal.fire({
            text: "Unable to send email",
            imageUrl: errorImage,
            customClass: "swal-success"
          });
        }
        setSelectedRows([]);
        setResetFilter(!resetFilter);
        setReload(!reload);
      }
    });
  };

  const handleAdd = () => {
    setAction("Add");
    setRowData(null);
    setShowForm(true);
  };

  const handleAddFromContract = () => {
    setShowContractPopup(true);
  };

  const handleDueDate = () => {
    const isFiltered = filteredData.length > 0 && filteredData.length < data.length;
    const dataToUpdate = isFiltered ? filteredData : selectedRows;
    if (dataToUpdate.length === 0) {
      MySwal.fire({
        text: "Please select at least one enquiry to update due date",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
      return;
    }
    setSelectedRows(dataToUpdate);
    setShowDatePicker(true);
  };

  const setPopUpData = async (data) => {
    if (data.name === "Contract Master") {
      setSelectedContract(data.result);
      setContractAction("Add");
      setShowContractPopup(false);
      setShowContractForm(true);
    } else if (data.name === "Customer Master") {
      setLoading(true);
      try {
        // Deduplicate selected customers by CustomerCode
        const selectedCustomers = data.result.filter((customer, index, self) =>
          index === self.findIndex(c => c.CustomerCode === customer.CustomerCode)
        );
        const existingLinked = selectedEnquiry.LinkedCustomers || [];
        const linkedCustomers = [];

        // data.result is the final list from popup (checked = linked, unchecked = delinked)
        for (let i = 0; i < selectedCustomers.length; i++) {
          const customer = selectedCustomers[i];
          // If already linked, reuse existing data (preserves LinkedUsers/MailSent)
          const existing = existingLinked.find(lc => lc.CustomerCode === customer.CustomerCode);
          if (existing) {
            linkedCustomers.push(existing);
            continue;
          }
          // New customer - fetch users
          try {
            const customerUsers = await getCustomerUsersByCode(customer.CustomerCode);

            // if (customerUsers.length === 0) {
            //   setLoading(false);
            //   MySwal.fire({
            //     text: `No active users found for customer ${customer.CustomerName}. Cannot link this customer.`,
            //     imageUrl: errorImage,
            //     customClass: "swal-error"
            //   });
            //   setShowCustomerPopup(false);
            //   return;
            // }

            const linkedUsers = customerUsers.map(user => ({
              UserEmail: user.email,
              UserName: user.userName,
              MailSent: false,
            }));

            linkedCustomers.push({
              CustomerCode: customer.CustomerCode,
              CustomerName: customer.CustomerName,
              LinkedUsers: linkedUsers
            });

          } catch (error) {
            console.error(`Error finding users for customer ${customer.CustomerCode}:`, error);
          }
        }

        const allCustomerNames = linkedCustomers.map(c => c.CustomerName);

        // Create CustomerUserName mapping for email personalization
        const customerUserName = {};
        const customerEmail = [];
        
        linkedCustomers.forEach(customer => {
          customer.LinkedUsers?.forEach(user => {
            customerUserName[user.UserEmail] = customer.CustomerName;
            customerEmail.push(user.UserEmail);
          });
        });

        // Optimized payload - send only required fields for customer linking
        const optimizedPayload = {
          LinkedBPName: allCustomerNames.join(", "),
          LinkedCustomers: linkedCustomers,
          CustomerUserName: customerUserName,
          CustomerEmail: customerEmail,
          EnquiryStatus: "Mail Not Sent",
          MailStatus: "Pending"
        };

        // Security check: Validate payload size to prevent malicious attacks
        const payloadSize = new Blob([JSON.stringify(optimizedPayload)]).size;
        if (payloadSize > 3 * 1024 * 1024) {
          setLoading(false);
          MySwal.fire({
            text: "Payload(data) size exceeds 3MB limit. Please reduce the number of linked customers.",
            imageUrl: errorImage,
            customClass: "swal-error"
          });
          setShowCustomerPopup(false);
          return;
        }

        const result = await linkCustomersToEnquiry(selectedEnquiry.EnquiryNumber, optimizedPayload);

        if (result.status) {
          MySwal.fire({
            text: "Customers linked successfully",
            imageUrl: successImage,
            customClass: "swal-success"
          });
          await addLogDetails(
            { Action: "LinkCustomer", EnquiryNumber: selectedEnquiry.EnquiryNumber, CustomerCount: selectedCustomers.length },
            "EnquiryTenderDashboard",
            selectedEnquiry.EnquiryNumber,
            "CustomerLinkLog",
            { userName: context.userName, userId: context.userId },
            "Transaction"
          );
          // Update local data state so grid and popup reflect changes immediately
          setData(prevData => prevData.map(item =>
            item.EnquiryNumber === selectedEnquiry.EnquiryNumber ? {
              ...item,
              LinkedBPName: optimizedPayload.LinkedBPName,
              LinkedCustomers: optimizedPayload.LinkedCustomers,
              CustomerUserName: optimizedPayload.CustomerUserName,
              CustomerEmail: optimizedPayload.CustomerEmail,
              EnquiryStatus: optimizedPayload.EnquiryStatus,
              MailStatus: optimizedPayload.MailStatus
            } : item
          ));
        } else {
          MySwal.fire({
            text: "Failed to link customers",
            imageUrl: errorImage,
            customClass: "swal-success"
          });
        }
      } catch (error) {
        console.error("Error linking customers:", error);
        MySwal.fire({
          text: "Failed to link customers",
          imageUrl: errorImage,
          customClass: "swal-success"
        });
      }
      setLoading(false);
      setShowCustomerPopup(false);
    }
  };

  const handleEdit = (row) => {
    if (row.EnquirySource === "Contract") {
      setSelectedContract(row);
      setContractAction("Edit");
      setShowContractForm(true);
    }
    else if (row.EnquirySource === "Disposal") {
      setSelectedDisposal(row);
      setDisposalAction("Edit");
      setShowDisposalForm(true);
    }
    else {
      setAction("Edit");
      setRowData(row);
      setShowForm(true);
    }
  };

  const handleView = (row) => {
    if (row.EnquirySource === "Contract") {
      setSelectedContract(row);
      setContractAction("View");
      setShowContractForm(true);
    }
    else if (row.EnquirySource === "Disposal") {
      setSelectedDisposal(row);
      setDisposalAction("View");
      setShowDisposalForm(true);
    }
    else {
      setAction("View");
      setRowData(row);
      setShowForm(true);
    }
  };


  const handleDelete = (row) => {
    console.log("the rowdata", row)
    MySwal.fire({
      text: "Are you sure you want to delete this enquiry?",
      confirmButtonText: "Delete",
      title: "Delete Enquiry",
      ...ALERT_PARAMS,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          // Prepare payload to mark as inactive
          row.Flag = "Inactive"
          row.EnquiryStatus = "Deleted"
          const result = await addEnquiryTender(row);

          if (result.status) {
            MySwal.fire({
              text: "Enquiry deleted successfully",
              imageUrl: successImage,
              customClass: "swal-success"
            });
            // Add log entry for delete action
            await addLogDetails(
              { Action: "Delete", EnquiryNumber: row.EnquiryNumber },
              "EnquiryTenderDashboard",
              row.EnquiryNumber,
              "DeleteLog",
              { userName: context.userName, userId: context.userId },
              "Transaction"
            );
            setReload(!reload);
          } else {
            MySwal.fire({
              text: result.message,
              imageUrl: errorImage,
              customClass: "swal-success"
            });
          }
        } catch (error) {
          MySwal.fire({
            text: "Unable to delete enquiry",
            imageUrl: errorImage,
            customClass: "swal-success"
          });
        }
        setLoading(false);
      }
    });
  };

  const handleCustomerLink = (row) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(row.DueDate);
    dueDate.setHours(0, 0, 0, 0);

    if (!row.DueDate || dueDate < today) {
      MySwal.fire({
        text: "Please update due date before linking customers",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
      return;
    }
    // Use latest row data from state to ensure LinkedCustomers is up-to-date
    const latestRow = data.find(d => d.EnquiryNumber === row.EnquiryNumber) || row;
    setSelectedEnquiry(latestRow);
    setShowCustomerPopup(true);
  };

  const columnDefs = [
    {
      headerName: "",
      field: "select",
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: "left",
      resizable: false,
    },
    {
      headerName: "Enquiry Status",
      field: "EnquiryStatus",
      minWidth: 120,
      tooltipField: "EnquiryStatus",
    },
    {
      headerName: "Enquiry Source",
      field: "EnquirySource",
      minWidth: 125,
      tooltipField: "EnquirySource",
    },
    {
      headerName: "Enquiry Source Number",
      field: "EnquirySourceNumber",
      minWidth: 180,
      tooltipField: "EnquirySource",
    },
    {
      headerName: "Enquiry Number",
      field: "EnquiryNumber",
      minWidth: 180,
      tooltipField: "EnquiryNumber",
    },
    {
      headerName: "Due Date",
      field: "DueDate",
      minWidth: 120,
      tooltipField: "DueDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (cellValue == null) return 0;
          const dateCell = new Date(cellValue);
          dateCell.setHours(0, 0, 0, 0);
          if (dateCell < filterLocalDateAtMidnight) return -1;
          if (dateCell > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 150,
      tooltipField: "ItemCode",
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 150,
      tooltipField: "ItemDescription",
    },
    {
      headerName: "Item Detail",
      field: "ItemDetail",
      minWidth: 150,
      tooltipField: "ItemDetail",
    },
    {
      headerName: "Item Type",
      field: "ItemType",
      minWidth: 120,
      tooltipField: "ItemType",
    },
    {
      headerName: "Quantity",
      field: "Quantity",
      minWidth: 120,
      tooltipField: "Quantity",
    },
    {
      headerName: "UOM",
      field: "UOM",
      minWidth: 100,
      tooltipField: "UOM",
    },
    {
      headerName: "GST%",
      field: "GST",
      minWidth: 100,
      tooltipField: "GST",
    },
    {
      headerName: "Division",
      field: "Division",
      minWidth: 120,
      tooltipField: "Division",
    },
    {
      headerName: "Location",
      field: "Location",
      minWidth: 120,
      tooltipField: "Location",
    },
    {
      headerName: "Logistic Company",
      field: "LogisticCompany",
      minWidth: 140,
      tooltipField: "LogisticCompany",
    },
    {
      headerName: "Images Attachment",
      field: "ImagesAttachment",
      minWidth: 120,
      tooltipField: "ImagesAttachment",
    },
    {
      headerName: "Remarks",
      field: "Remarks",
      minWidth: 150,
      tooltipField: "Remarks",
    },
    {
      headerName: "Linked BP Name",
      field: "LinkedBPName",
      minWidth: 150,
      tooltipField: "LinkedBPName",
    },
    {
      headerName: "Mail Status",
      field: "MailStatus",
      minWidth: 150,
      tooltipField: "MailStatus",
    },
    {
      headerName: "Options",
      field: "",
      width: 150,
      cellRenderer: (params) => (
        <ActionRenderer
          selectedData={params.data}
          handleEditEvent={() => handleEdit(params.data)}
          handleDeleteEvent={() => handleDelete(params.data)}
          handleCustomerLinkEvent={() => handleCustomerLink(params.data)}
          handleViewEvent={() => handleView(params.data)}
          showEdit={true}
          showView={false}
          showDelete={true}
        />
      ),
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    }
  ];

  const headerName = (field) => {
    const mapping = {
      "EnquiryStatus": "Enquiry Status",
      "EnquirySource": "Enquiry Source",
      "EnquiryNumber": "Enquiry Number",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "ItemDetail": "Item Detail",
      "ItemType": "Item Type",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST": "GST%",
      "Location": "Location",
      "Division": "Division",
      "LogisticCompany": "Logistic Company",
      "ImagesAttachment": "Images Attachment",
      "Remarks": "Remarks",
      "DueDate": "Due Date",
      "LinkedBPName": "Linked BP Name",
      "MailStatus": "Mail Status"
    };
    return mapping[field] || "No match found";
  };

  const fieldName = (header) => {
    const mapping = {
      "Enquiry Status": "EnquiryStatus",
      "Enquiry Source": "EnquirySource",
      "Enquiry Number": "EnquiryNumber",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Item Detail": "ItemDetail",
      "Item Type": "ItemType",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST%": "GST",
      "Location": "Location",
      "Division": "Division",
      "Logistic Company": "LogisticCompany",
      "Images Attachment": "ImagesAttachment",
      "Remarks": "Remarks",
      "Due Date": "DueDate",
      "Linked BP Name": "LinkedBPName",
      "Mail Status": "MailStatus"
    };
    return mapping[header] || "No match found";
  };

  return (
    <>
      {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <>
          <ActionComp
            label={"Add New Enquiry/Tender"}
            handleAdd={handleAdd}
            label2={"Add Enquiry (Contracts)"}
            handleAdd2={handleAddFromContract}
            label3={"Send Mail"}
            handleAdd3={handleSendMail}
            labelDueDate={"Update Due Date"}
            handleDueDate={handleDueDate}
          />
          <GridMaster
            rowDetails={data}
            colDetails={columnDefs}
            fieldNames={fieldName}
            headerNames={headerName}
            getDataEvent={() => { }}
            getFilterColLength={() => { }}
            setExcelData={() => { }}
            resetind={resetFilter}
            rowSelection="multiple"
            onSelectionChanged={(rows) => setSelectedRows(rows)}
            onFilteredDataChange={setFilteredData}
          />
        </>
        {showForm && (
          <AddEditEnquiryTender
            show={showForm}
            action={action}
            rowData={rowData}
            onHide={() => setShowForm(false)}
            setReload={setReload}
            reload={reload}
          />
        )}
        {showContractPopup && (
          <SingleValuePopUp
            show={showContractPopup}
            onHide={() => setShowContractPopup(false)}
            setSelected={setPopUpData}
            mastername="Contract Master"
            paramkey="ContractCode"
            paramvalue=""
            backdrop="static"
          />
        )}
        {showContractForm && (
          <AddEditEnquiryFromContract
            show={showContractForm}
            contractData={selectedContract}
            onHide={() => setShowContractForm(false)}
            setReload={setReload}
            reload={reload}
            action={contractAction}
          />
        )}
        {showDisposalForm && (
          <AddEditEnquiryFromDisposal
            show={showDisposalForm}
            disposalData={selectedDisposal}
            onHide={() => setShowDisposalForm(false)}
            setReload={setReload}
            reload={reload}
            action={disposalAction}
          />
        )}
        {showCustomerPopup && (
          <MultiValuePopUp
            show={showCustomerPopup}
            onHide={() => setShowCustomerPopup(false)}
            setSelected={setPopUpData}
            masterName="Customer Master"
            selectedItems={(() => {
              const uniqueCodes = [...new Set(selectedEnquiry?.LinkedCustomers?.map(c => c.CustomerCode) || [])];
              return uniqueCodes.join(", ");
            })()}
          />
        )}
        {showDatePicker && (
          <UpdateDueDateModal
            show={showDatePicker}
            onHide={() => setShowDatePicker(false)}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            setReload={setReload}
            reload={reload}
            setLoading={setLoading}
            loading={loading}
          />
        )}
      </Container>
    </>
  );
};

export default EnquiryTenderDashboard;
